import React from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Button,
    Flex,
    useStyleConfig,
    useBoolean,
    useMediaQuery,
    Img,
    AspectRatio,
} from '@chakra-ui/react'
import {remCalc} from '../../../theme/_utils'
import {CloseButton} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Autoplay, Navigation, Pagination} from 'swiper/modules'
import {Swiper, SwiperSlide} from 'swiper/react'
import {ChevronLeft, ChevronRight} from '../../icons/arrows'
import {StoriesSwiperSlide} from './stories-swiper-slide'
import useDamImage from '../../../custom-hooks/use-dam-image'

export const Stories = ({
    screenStart,
    storiesGroup = [],
    screenEnd,
    hasCloseButton = true,
    autoplayDelay = 10,
    maskgroup,
    maxWidth = remCalc(1920),
}) => {
    const [sliderVisible, setSliderVisible] = useBoolean(false)
    const [sliderEndReached, setSliderEndReached] = useBoolean(false)

    const [isMobile] = useMediaQuery(`(max-width: ${remCalc(449)})`)

    const styles = useStyleConfig('Stories', {autoplayDelay, maskgroup})

    const swiperStyles = useStyleConfig('Swiper')
    const swiperHasNavigation = storiesGroup.length > 1

    const screenStartImage = useDamImage(screenStart.image)
    const screenEndImage = useDamImage(screenEnd.image)

    return (
        <Flex data-testid="stories" __css={{...styles.container}} maxW={maxWidth}>
            <Flex data-testid="stories-slider" __css={{...styles.slidercontainer}}>
                <Box
                    display={!sliderVisible && !sliderEndReached ? 'block' : 'none'}
                    {...styles.mediaContainer}
                >
                    <AspectRatio ratio={screenStartImage.ratio} width={'100%'}>
                        <Img
                            src={screenStartImage.url}
                            alt={screenStart.image.alt || ''}
                            {...styles.screenImg}
                        />
                    </AspectRatio>
                    <Flex {...styles.screenText} direction={'column'}>
                        {screenStart.heading && (
                            <Box
                                {...styles.heading}
                                color={'text.inverse'}
                                textAlign={'center'}
                                textTransform={'none'}
                                textStyle={'display-small'}
                                fontWeight={'medium'}
                            >
                                {screenStart.heading}
                            </Box>
                        )}
                        {screenStart?.button?.cta && (
                            <Box textAlign={'center'}>
                                <Button
                                    to="#"
                                    variant={'inverse-secondary'}
                                    textStyle={'body-medium'}
                                    fontWeight={'bold'}
                                    minW={{base: remCalc(214), lg: remCalc(230), md: remCalc(310)}}
                                    onClick={() => {
                                        setSliderVisible.on()
                                        setSliderEndReached.off()
                                    }}
                                >
                                    {screenStart.button.cta.title}
                                </Button>
                            </Box>
                        )}
                    </Flex>
                </Box>

                <Box
                    display={sliderVisible && !sliderEndReached ? 'block' : 'none'}
                    __css={swiperStyles}
                    {...styles.mediaContainer}
                >
                    {hasCloseButton && (
                        <CloseButton
                            size={{base: 'sm', sm: 'md', md: 'lg', lg: 'lg'}}
                            onClick={() => {
                                setSliderVisible.off()
                                setSliderEndReached.off()
                            }}
                            {...styles.closeButton}
                        ></CloseButton>
                    )}
                    <Swiper
                        key={isMobile ? 'mobile-view' : 'other-view'}
                        modules={[Autoplay, Navigation, Pagination]}
                        navigation={{
                            prevEl: `.swiper-button-prev`,
                            nextEl: `.swiper-button-next`,
                        }}
                        pagination={{
                            el: '.stories-slider-pagination',
                            bulletClass: 'stories-pagination-bullet',
                            bulletActiveClass: 'stories-pagination-bullet-active',
                            renderBullet: (index, className) =>
                                `<span class="${className} bullet-${index}"><span></span></span>`,
                            clickable: true,
                        }}
                        keyboard={{
                            enabled: false,
                        }}
                        autoplay={{
                            delay: autoplayDelay * 1000,
                        }}
                        loop={false}
                        enabled={true}
                        slidesPerView={1}
                        spaceBetween={0}
                        onReachBeginning={(swiper) => {
                            swiper.pagination.bullets[0].classList.remove('full')
                            swiper.pagination.bullets[0].classList.add('current')
                            swiper.slides[0].firstChild.classList.add('current')
                        }}
                        onSlideChange={(swiper) => {
                            let activeIndex = swiper.activeIndex
                            swiper.pagination.bullets.forEach((b, i) => {
                                if (i < activeIndex) {
                                    b.classList.remove('current')
                                    b.classList.add('full')
                                }
                                if (i == activeIndex) {
                                    b.classList.remove('full')
                                    b.classList.add('current')
                                }
                                if (i > activeIndex) {
                                    b.classList.remove('full')
                                    b.classList.remove('current')
                                }
                            })
                            swiper.slides.forEach((s, i) => {
                                s.firstChild.classList.remove('current')
                                if (i == activeIndex) {
                                    s.firstChild.classList.add('current')
                                }
                            })
                        }}
                        onReachEnd={(swiper) => {
                            setTimeout(() => {
                                if (swiper.isEnd) {
                                    swiper.pagination.bullets.forEach((b) => {
                                        b.classList.remove('full')
                                        b.classList.remove('current')
                                    })
                                    swiper.slides.forEach((s) => {
                                        s.firstChild.classList.remove('current')
                                        s.classList.remove('swiper-slide-prev')
                                        s.classList.remove('swiper-slide-active')
                                        s.classList.remove('swiper-slide-next')
                                    })
                                    setSliderVisible.off()
                                    setSliderEndReached.on()
                                }
                            }, autoplayDelay * 1000)
                        }}
                    >
                        {storiesGroup.map((group, gIndex) => (
                            <SwiperSlide key={`stories-slide-${gIndex}`}>
                                <StoriesSwiperSlide
                                    styles={styles}
                                    title={group.title}
                                    bodyCopy={group.body_copy}
                                    isVideo={group.media?.is_video}
                                    video={
                                        group.media?.video && group.media?.video.length
                                            ? group.media?.video[0]
                                            : null
                                    }
                                    image={group.media?.image}
                                />
                            </SwiperSlide>
                        ))}
                        {swiperHasNavigation && (
                            <ChevronLeft
                                className="swiper-button-prev"
                                aria-label="swiper button prev icon"
                                {...styles.chevronIcon}
                                left={remCalc(24)}
                            />
                        )}
                        {swiperHasNavigation && (
                            <ChevronRight
                                className="swiper-button-next"
                                aria-label="swiper button next icon"
                                {...styles.chevronIcon}
                                right={remCalc(24)}
                            />
                        )}
                        <Flex
                            className="stories-slider-pagination"
                            {...styles.pagination}
                            direction={'row'}
                        />
                    </Swiper>
                </Box>

                <Box
                    display={screenEnd && !sliderVisible && sliderEndReached ? 'block' : 'none'}
                    {...styles.mediaContainer}
                >
                    <AspectRatio ratio={screenEndImage.ratio} width={'100%'}>
                        <Img
                            src={screenEndImage.url}
                            alt={screenEnd.image.alt || ''}
                            {...styles.screenImg}
                        />
                    </AspectRatio>
                    <Flex {...styles.screenText} direction={'column'}>
                        {screenEnd?.heading && (
                            <Box
                                {...styles.heading}
                                textAlign={'center'}
                                textTransform={'none'}
                                color={'text.inverse'}
                                textStyle={'display-small'}
                                fontWeight={'medium'}
                            >
                                {screenEnd.heading}
                            </Box>
                        )}
                        {screenEnd?.button?.cta && (
                            <Box textAlign={'center'}>
                                <Button
                                    to="#"
                                    variant={'inverse-secondary'}
                                    textStyle={'body-medium'}
                                    fontWeight={'bold'}
                                    minW={{base: remCalc(214), lg: remCalc(230), md: remCalc(310)}}
                                    onClick={() => {
                                        setSliderVisible.on()
                                        setSliderEndReached.off()
                                    }}
                                >
                                    {screenEnd.button.cta.title}
                                </Button>
                            </Box>
                        )}
                    </Flex>
                    {hasCloseButton && (
                        <CloseButton
                            size={{base: 'sm', sm: 'md', md: 'lg', lg: 'lg'}}
                            onClick={() => {
                                setSliderVisible.off()
                                setSliderEndReached.off()
                            }}
                            {...styles.closeButton}
                        ></CloseButton>
                    )}
                </Box>
            </Flex>
        </Flex>
    )
}

Stories.propTypes = {
    screenStart: PropTypes.shape({
        heading: PropTypes.string,
        button: PropTypes.shape(),
        image: PropTypes.shape(),
    }),
    storiesGroup: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string,
            body_copy: PropTypes.string,
            media: PropTypes.shape(),
        }),
    ),
    screenEnd: PropTypes.shape({
        heading: PropTypes.string,
        button: PropTypes.shape(),
        image: PropTypes.shape(),
    }),
    hasCloseButton: PropTypes.bool,
    autoplayDelay: PropTypes.number,
    maskgroup: PropTypes.shape({
        maskheaderactive: PropTypes.bool,
        opacity: PropTypes.string,
    }),
    maxWidth: PropTypes.string,
}
